import type { NotificationResponseDto } from '@/lib/models';
import { metaBool, metaNumber, metaString, type NotificationMeta } from './meta';
import type { NotificationType } from './notification-types';

/**
 * The per-view key the detail dispatcher resolves a notification to. Each key
 * has one pop-up view component in the registry. `MARKET_RESOLUTION` fans out to
 * three keys by outcome, and `ORDER_FILLED` fans out to two by fill state.
 * `generic` is the catch-all template for the non-PRD types (announcement /
 * promotion / monetization / signup / comment).
 */
export type PopupViewKey =
  | 'withdrawal-approved'
  | 'withdrawal-failed'
  | 'resolution-won-all'
  | 'resolution-won-some'
  | 'resolution-none'
  | 'position-value-change'
  | 'order-filled'
  | 'order-filled-partial'
  | 'new-market'
  | 'limit-order-expired'
  | 'market-closing-soon'
  | 'generic';

/** The 11 PRD pop-up keys (everything except `generic`) — used to pre-register views. */
export const PRD_POPUP_KEYS: Exclude<PopupViewKey, 'generic'>[] = [
  'withdrawal-approved',
  'withdrawal-failed',
  'resolution-won-all',
  'resolution-won-some',
  'resolution-none',
  'position-value-change',
  'order-filled',
  'order-filled-partial',
  'new-market',
  'limit-order-expired',
  'market-closing-soon',
];

/** Market-resolution outcome (frozen API contract, on `meta.resolutionOutcome`). */
export type ResolutionOutcome = 'WON_ALL' | 'WON_SOME' | 'NONE';

/**
 * Resolve the resolution outcome from `meta`. Prefers the frozen
 * `meta.resolutionOutcome` enum; falls back to the legacy `meta.won` boolean
 * (true → all won, false → none) so pre-contract producers still route sanely.
 *
 * A missing/ambiguous meta defaults to `NONE` (the neutral "Market Resolved"
 * view), NOT `WON_ALL`: this is a real-money settlement surface, so the safe
 * failure mode for malformed/legacy data is neutral, never a false celebratory
 * "You called it!" trophy the player didn't earn.
 */
export function resolutionOutcome(meta: NotificationMeta): ResolutionOutcome {
  const outcome = metaString(meta, 'resolutionOutcome');
  if (outcome === 'WON_ALL' || outcome === 'WON_SOME' || outcome === 'NONE') return outcome;
  const won = metaBool(meta, 'won');
  if (won === true) return 'WON_ALL';
  if (won === false) return 'NONE';
  return 'NONE';
}

/**
 * True when an `ORDER_FILLED` notification represents a *partial* fill (routes
 * to the "You're in — partly" view). Prefers explicit signals
 * (`meta.fillStatus === 'PARTIAL'` / `meta.partial === true`), otherwise infers
 * it from `filledQuantity < orderQuantity`.
 */
export function isPartialFill(meta: NotificationMeta): boolean {
  if (metaString(meta, 'fillStatus') === 'PARTIAL') return true;
  if (metaBool(meta, 'partial') === true) return true;
  const filled = metaNumber(meta, 'filledQuantity');
  const ordered = metaNumber(meta, 'orderQuantity');
  return filled !== undefined && ordered !== undefined && filled < ordered;
}

/**
 * Map a notification to its pop-up view key. Pure — unit-tested independently of
 * the component registry (`popup-registry.tsx`).
 */
export function resolvePopupViewKey(notification: Pick<NotificationResponseDto, 'type' | 'meta'>): PopupViewKey {
  const type = notification.type as NotificationType;
  const meta = notification.meta;

  switch (type) {
    case 'WITHDRAW_APPROVED':
      return 'withdrawal-approved';
    case 'WITHDRAW_FAILED':
      return 'withdrawal-failed';
    case 'MARKET_RESOLUTION': {
      const outcome = resolutionOutcome(meta);
      return outcome === 'WON_ALL'
        ? 'resolution-won-all'
        : outcome === 'WON_SOME'
          ? 'resolution-won-some'
          : 'resolution-none';
    }
    case 'POSITION_VALUE_CHANGE':
      return 'position-value-change';
    case 'ORDER_FILLED':
      return isPartialFill(meta) ? 'order-filled-partial' : 'order-filled';
    case 'NEW_MARKET':
      return 'new-market';
    case 'LIMIT_ORDER_EXPIRED':
      return 'limit-order-expired';
    case 'MARKET_CLOSING_SOON':
      return 'market-closing-soon';
    default:
      return 'generic';
  }
}
